import { useQuery } from '@apollo/react-hooks';
import { NetworkStatus } from 'apollo-client';
import gql from 'graphql-tag';
import get from 'lodash/get';

import { fragments as filterFragments, workIsSafe } from '../../../lib/filters';

const {
  loading: LOADING,
  fetchMore: FETCH_MORE,
  refetch: REFETCH
} = NetworkStatus;

const recentWorksQuery = (customFragments = []) => {
  const allFragments = [...filterFragments, ...customFragments];
  const workFragments = allFragments.filter(({ type }) => type === 'Work');

  return gql`
    query withRecentWorksByArtists($artistIds: [Int!]!, $page: Int = 1, $locale: String!, $searchType: String) {
      recentWorksByArtists(artistIds: $artistIds, page: $page, searchType: $searchType) {
        works(locale: $locale) {
          ${workFragments.map(({ name }) => `...${name}`).join('\n')}
        }
        page
        hasMore
      }
    }
    ${workFragments.reduce(
      (acc, { fragment }) => gql`
        ${acc}
        ${fragment}
      `,
      ''
    )}
  `;
};

export const useRecentWorksByArtists = ({
  fragments,
  locale,
  artistIds,
  ssr = false,
  searchType
}) => {
  const query = recentWorksQuery(fragments);
  const skip = !Array.isArray(artistIds) || artistIds.length === 0;
  const { data, error, networkStatus, refetch, fetchMore } = useQuery(query, {
    variables: { page: 1, artistIds, locale, searchType },
    errorPolicy: 'all',
    ssr,
    notifyOnNetworkStatusChange: true,
    skip
  });

  const works = get(data, 'recentWorksByArtists.works', []).filter(workIsSafe);
  const page = get(data, 'recentWorksByArtists.page', 1);
  const hasMore = get(data, 'recentWorksByArtists.hasMore');
  return {
    works,
    hasMore,
    error,
    loading: [LOADING, REFETCH].includes(networkStatus) && !skip,
    refetch,
    fetchingMore: networkStatus === FETCH_MORE,
    fetchMore: () =>
      fetchMore({
        variables: { page: page + 1 },
        updateQuery: (prevResult, { fetchMoreResult }) => {
          const prevWorks = get(prevResult, 'recentWorksByArtists.works');
          const newWorks = get(fetchMoreResult, 'recentWorksByArtists.works');

          return {
            ...fetchMoreResult,
            recentWorksByArtists: {
              ...get(fetchMoreResult, 'recentWorksByArtists'),
              works: [
                ...prevWorks,
                ...newWorks.filter(
                  w => !prevWorks.some(({ id }) => id === w.id)
                )
              ]
            }
          };
        }
      })
  };
};

export default useRecentWorksByArtists;
